/* eslint-disable max-lines
-- Disabled to set CI to fail on this issue on new files, PR #6757*/
import { createContext, useCallback, useContext, useMemo } from "react";
import { useQueryClient } from "react-query";
import { useHistory } from "react-router-dom";
import { t } from "@/i18n-js/instance";
import {
  reactQueryDelete,
  reactQueryPatch,
} from "@/react/helpers/backendRequestHelpers";
import { notifyBugsnag } from "@/react/helpers/bugsnagHelpers";
import type { ChatRoom } from "@/react/types/Chat/ChatRoom";
import { chatRoomsPath, internalApi } from "@circle-react/helpers/urlHelpers";
import { useToast } from "@circle-react-uikit/ToastV2";
import { DIRECT_CHAT_ROOM } from "../../components/constants";
import { embeddableChatApiQueryKey } from "../embeddable_chat/useEmbeddableChatsApi";
import { useDocumentTitle } from "../notifications/useDocumentTitle";
import { useDebounceApi } from "../useDebounceApi";
import { selectActions, selectChatRooms } from "./store";
import { useChatStore } from "./store/useChatStore";
import { useMarkChatRoomAsRead } from "./useMarkChatRoomAsRead";

const MARK_READ_DEBOUNCE_TIMEOUT = 2000;

const initialState = {
  chatRooms: [],
};

const ChatRoomsContext = createContext(initialState);
ChatRoomsContext.displayName = "ChatRoomsContext";

export const ChatRoomsContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const chatRooms = useChatStore(selectChatRooms);
  const {
    fetchChatRoom,
    updateChatRoom,
    appendChatRoom,
    removeChatRoom,
    markChatRoomAsRead,
  } = useChatStore(selectActions);
  const { addDirectMessageParticipantNameInTitle } = useDocumentTitle();
  const { markChatRoomAsRead: markChatRoomMessagesAsRead } =
    useMarkChatRoomAsRead();
  const history = useHistory();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { debouncedFunction } = useDebounceApi();

  const fetchChatRoomForUuid = useCallback(
    (uuid: string) => chatRooms.find(chatRoom => chatRoom.uuid === uuid),
    [chatRooms],
  );

  const onNewMessage = useCallback(
    (eventData: any, communityMemberId: number, isOpenChatRoom: boolean) => {
      const latestChatRoomMessage = eventData.json_message;
      const {
        chat_room_uuid,
        sender: { community_member_id },
        embedded,
        parent_message_id,
      } = latestChatRoomMessage;

      if (parent_message_id) return;
      const messageChatRoom: ChatRoom | undefined =
        fetchChatRoomForUuid(chat_room_uuid);
      if (messageChatRoom) {
        // If chat room is open then mark all messages as read
        // isOpen is not available for chat spaces
        if (isOpenChatRoom) {
          //Debounce the API call to mark all messages as read
          debouncedFunction(
            markChatRoomMessagesAsRead,
            MARK_READ_DEBOUNCE_TIMEOUT,
            { uuid: chat_room_uuid },
          );
        }

        //community_member_id: sender of the message
        //communityMemberId: current logged in community member
        const hasUnreadMessagesAfterNewMessage =
          !isOpenChatRoom && community_member_id !== communityMemberId;

        const updatedChatRoom = {
          ...messageChatRoom,
          last_message: latestChatRoomMessage,
          has_unread_messages: hasUnreadMessagesAfterNewMessage,
        };

        // The chat room with latest message will be at the top of the list
        appendChatRoom(updatedChatRoom);
      } else {
        // The message is received in the chat room which is not present in the chatRooms state.
        // So we need to fetch the chat room data from backend and add it to chatRooms
        if (embedded !== true) {
          void fetchChatRoom(chat_room_uuid);
        }
      }
    },
    [appendChatRoom, debouncedFunction, fetchChatRoom, fetchChatRoomForUuid],
  );

  const onChatRoomRead = useCallback(
    (eventData: any) => {
      const { chat_room_uuid: uuid } = eventData;
      markChatRoomAsRead(uuid);
    },
    [markChatRoomAsRead],
  );

  const onChatRoomCreated = useCallback(
    (eventData: any) => {
      const { chat_room: chatRoom } = eventData || {};
      appendChatRoom(chatRoom);
    },
    [appendChatRoom],
  );

  const onChatRoomUpdated = useCallback(
    (eventData: any) => {
      const { chat_room: chatRoom } = eventData || {};
      updateChatRoom(chatRoom);
    },
    [updateChatRoom],
  );

  const onChatRoomDetailsUpdated = useCallback(
    (eventData: any = {}) => {
      const { chat_room: chatRoom } = eventData;
      updateChatRoom(chatRoom);
    },
    [updateChatRoom],
  );

  const onChatRoomParticipantsUpdated = useCallback(
    (eventData: any) => {
      const updatedChatRoom =
        typeof eventData.chat_room === "object"
          ? eventData.chat_room
          : JSON.parse(eventData.chat_room);
      updateChatRoom(updatedChatRoom);

      // Need to refetch chat room if current participant was updated
      // This is necessary to reflect changes in the UI
      void queryClient.invalidateQueries(
        embeddableChatApiQueryKey(updatedChatRoom.uuid),
      );
    },
    [queryClient, updateChatRoom],
  );

  const onChatRoomParticipantDestroy = useCallback(
    (eventData: any) => {
      const { chat_room_uuid: uuid } = eventData;
      removeChatRoom(uuid);
      history.push(chatRoomsPath());
    },
    [history, removeChatRoom],
  );

  const onEventReceive = useCallback(
    (eventData: any, communityMemberId: number, isOpenChatRoom: boolean) => {
      switch (eventData.event) {
        case "newMessage":
          onNewMessage(eventData, communityMemberId, isOpenChatRoom);
          break;
        case "chatRoomRead":
          onChatRoomRead(eventData);
          break;
        case "chatRoomCreated":
          onChatRoomCreated(eventData);
          break;
        case "chatRoomUpdated":
          onChatRoomUpdated(eventData);
          break;
        case "chatRoomParticipantsUpdated":
          onChatRoomParticipantsUpdated(eventData);
          break;
        case "chatRoomParticipantDestroy":
          onChatRoomParticipantDestroy(eventData);
          break;
        case "chatRoomDeleted":
          onChatRoomParticipantDestroy(eventData);
      }
    },
    [
      onChatRoomCreated,
      onChatRoomParticipantDestroy,
      onChatRoomParticipantsUpdated,
      onChatRoomRead,
      onChatRoomUpdated,
      onNewMessage,
    ],
  );

  const onEmbedEventReceive = useCallback(
    (eventData: any) => {
      switch (eventData.event) {
        case "chatRoomRead":
          onChatRoomRead(eventData);
          break;
        case "chatRoomUpdated":
          onChatRoomDetailsUpdated(eventData);
          break;
        case "chatRoomParticipantsUpdated":
          onChatRoomParticipantsUpdated(eventData);
          break;
        case "chatRoomParticipantDestroy":
          onChatRoomParticipantDestroy(eventData);
          break;
      }
    },
    [
      onChatRoomDetailsUpdated,
      onChatRoomParticipantDestroy,
      onChatRoomParticipantsUpdated,
      onChatRoomRead,
    ],
  );

  const updateTitleBar = useCallback(
    (uuid: string) => {
      const openChatRoom: ChatRoom | undefined = fetchChatRoomForUuid(uuid);
      if (openChatRoom) {
        addDirectMessageParticipantNameInTitle(
          openChatRoom.chat_room_kind === DIRECT_CHAT_ROOM
            ? openChatRoom.other_participants_preview?.[0]?.name
            : openChatRoom.chat_room_name,
        );
      }
    },
    [addDirectMessageParticipantNameInTitle],
  );

  const updateRichTextMessage = useCallback(
    async ({
      chatRoomUuid,
      rich_text_body,
      messageId,
      unfurl_urls,
    }: {
      chatRoomUuid: string;
      rich_text_body: any;
      messageId: number;
      unfurl_urls: { [key: string]: boolean };
    }) => {
      const chatRoomMessageParams: any = {
        rich_text_body,
      };

      if (unfurl_urls) {
        chatRoomMessageParams["unfurl_urls"] = unfurl_urls;
      }
      try {
        await reactQueryPatch(
          internalApi.chatRoomMessages.update({
            uuid: chatRoomUuid,
            id: messageId,
          }),
          {
            chat_room_message: chatRoomMessageParams,
          },
        );
      } catch (err: any) {
        toast.error(t("something_went_wrong"));
        notifyBugsnag(err);
      }
    },
    [toast],
  );

  const deleteRichTextMessage = useCallback(
    async ({
      chatRoomUuid,
      messageId,
    }: {
      chatRoomUuid: string;
      messageId: number;
    }) => {
      try {
        await reactQueryDelete(
          internalApi.chatRoomMessages.delete({
            uuid: chatRoomUuid,
            id: messageId,
          }),
        );
      } catch (err: any) {
        toast.error(t("something_went_wrong"));
        notifyBugsnag(err);
      }
    },
    [toast],
  );

  const value: any = useMemo(
    () => ({
      chatRooms,
      fetchChatRoomForUuid,
      onEventReceive,
      onEmbedEventReceive,
      updateTitleBar,
      updateRichTextMessage,
      deleteRichTextMessage,
    }),
    [
      chatRooms,
      deleteRichTextMessage,
      fetchChatRoomForUuid,
      onEmbedEventReceive,
      onEventReceive,
      updateRichTextMessage,
      updateTitleBar,
    ],
  );

  return (
    <ChatRoomsContext.Provider value={value}>
      {children}
    </ChatRoomsContext.Provider>
  );
};

export const useChatsV2Api = () => useContext(ChatRoomsContext);
